import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { userService } from '@/api/services/userService'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { toast } from '@/store/useToastStore'
import { AddressPicker, ADDRESS_DEFAULT_CENTER, EMPTY_ADDRESS, type AddressValue } from './AddressPicker'

type Props = {
  open: boolean
  onClose: () => void
  userId: number | string
  address?: any | null   // null/undefined → add mode
  onSaved: () => void
}

export function AddressModal({ open, onClose, userId, address, onSaved }: Props) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const isEdit = !!address?.id

  const [form, setForm] = useState<AddressValue>(EMPTY_ADDRESS)
  const [pos, setPos] = useState(ADDRESS_DEFAULT_CENTER)
  const [saving, setSaving] = useState(false)

  // Initialise the form whenever the modal opens / target address changes
  useEffect(() => {
    if (!open) return
    setForm({
      label:             address?.label ?? '',
      building_name:     address?.building_name ?? '',
      plot_number:       address?.plot_number ?? '',
      street:            address?.street ?? address?.address ?? '',
      landmark:          address?.landmark ?? '',
      phone_number:      address?.phone_number ?? '',
      country_code:      address?.country_code ?? '971',
      city_id:           address?.city_id ?? '',
      state_province_id: address?.state_province_id ?? '',
      postal_code:       address?.postal_code ?? '',
      additional_notes:  address?.additional_notes ?? '',
      neighbourhood:     address?.neighbourhood ?? '',
      is_default:        !!address?.is_default,
    })
    const lat = Number(address?.latitude), lng = Number(address?.longitude)
    setPos(Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0) ? { lat, lng } : ADDRESS_DEFAULT_CENTER)
  }, [open, address])

  if (!open) return null

  const save = async () => {
    if (!form.street) { toast.error(t('addressModal.streetRequired')); return }
    if (isEdit && !(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        ...(isEdit ? { id: address.id } : {}),
        user_id:           userId,
        label:             form.label,
        building_name:     form.building_name,
        plot_number:       form.plot_number,
        street:            form.street,
        address:           form.street,
        landmark:          form.landmark,
        neighbourhood:     form.neighbourhood,
        phone_number:      form.phone_number,
        country_code:      form.country_code,
        city_id:           form.city_id || undefined,
        state_province_id: form.state_province_id || undefined,
        postal_code:       form.postal_code,
        additional_notes:  form.additional_notes,
        latitude:          pos.lat,
        longitude:         pos.lng,
        is_default:        form.is_default ? 1 : 0,
      }
      await userService.saveAddress(payload)
      toast.success(isEdit ? t('addressModal.addressUpdated') : t('addressModal.addressAdded'))
      onSaved()
      onClose()
    } catch (e: any) {
      const errs = e?.response?.data?.errors
      toast.error(errs
        ? (typeof errs === 'string' ? errs : Object.values(errs).flat().join(', '))
        : (e?.response?.data?.message ?? t('addressModal.failedSaveAddress')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900">{isEdit ? t('addressModal.editAddressTitle') : t('addressModal.addNewAddressTitle')}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <div className="p-5">
          <AddressPicker
            value={form}
            onChange={patch => setForm(f => ({ ...f, ...patch }))}
            pos={pos}
            onPosChange={setPos}
            active={open}
          />
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">{t('common.cancel')}</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50" style={{ background: '#f68b2c' }}>
            {saving ? t('addressModal.saving') : isEdit ? t('addressModal.updateAddress') : t('addressModal.addAddress')}
          </button>
        </div>
      </div>
    </div>
  )
}
